import {
  useVersionDeleteMutation,
  GetVersionHistoryDocument,
  GetVersionHistoryQuery,
  GetVersionHistoryQueryVariables,
} from 'gqlTypes'
import { ErrorHandler } from 'modules/tracing'
import {
  shouldReduceTotalCount,
  recordInitiatedDeletion,
} from './deletedVersionsTracking'

const GENERIC_ERROR_MESSAGE =
  'Something is wrong, please contact our support team'

interface UseVersionDeleteProps {
  versionIdentifier: string
  onError(message: string): void
  onCompleted(): void
  shareIdentifier: string
}

export const useVersionDelete = (props: UseVersionDeleteProps) => {
  const { onError, versionIdentifier, onCompleted, shareIdentifier } = props

  const [mutate, ...rest] = useVersionDeleteMutation({
    redirectErrors: true,
    variables: { versionIdentifier },
    onCompleted: data => {
      if (!data || !data.versionDelete) {
        onError(GENERIC_ERROR_MESSAGE)
        return
      }

      onCompleted()
    },
    update: (cache, { data }) => {
      if (!data?.versionDelete?.versionDeleted) {
        ErrorHandler.shouldNeverHappen.invalidMutationData('versionDelete')
        return
      }

      try {
        const versionHistory = cache.readQuery<
          GetVersionHistoryQuery,
          GetVersionHistoryQueryVariables
        >({
          query: GetVersionHistoryDocument,
          variables: { shareIdentifier, after: null },
        })!

        if (!versionHistory.share) {
          ErrorHandler.shouldNeverHappen(
            'versionHistory data should be loaded when deleting version'
          )
          return
        }

        const entries = versionHistory.share.versionHistory.entries
        const index = entries.findIndex(x => x.identifier === versionIdentifier)
        entries[index] = data.versionDelete.versionDeleted

        if (shouldReduceTotalCount(versionIdentifier)) {
          versionHistory.share.availableVersions.meta.totalCount--
        }

        cache.writeQuery<
          GetVersionHistoryQuery,
          GetVersionHistoryQueryVariables
        >({
          data: versionHistory,
          query: GetVersionHistoryDocument,
          variables: { shareIdentifier, after: null },
        })
      } catch (error) {
        ErrorHandler.shouldNeverHappen(
          'failed to update versionHistory data after deleting version'
        )
        onError(GENERIC_ERROR_MESSAGE)
      }
    },
    onError: error => {
      onError(error.message)
    },
  })

  const augmentedMutate: typeof mutate = (...args) => {
    recordInitiatedDeletion(versionIdentifier)
    return mutate(...args)
  }

  return [augmentedMutate, ...rest] as const
}
